<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ClipboardList, GripVertical, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { useDragSort } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import {
  createReferenceItem,
  deleteReferenceItem,
  listOperationReferences,
  updateReferenceItem,
} from '../productService'
import type { OperationReference } from '../types'

const client = useApiClient()
const auth = useAuthStore()
const { t, locale } = useI18n()
const operations = ref<OperationReference[]>([])
const loading = ref(false)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const deleting = ref(false)
const errorMessage = ref('')
const formError = ref('')
const selectionMessage = ref('')
const search = ref('')
const editingId = ref<number | null>(null)
const currentPage = ref(1)
const pageSize = ref(10)
const selectedOperationIds = ref<number[]>([])
let searchTimer: ReturnType<typeof setTimeout> | null = null
const form = reactive({
  code: '',
  name: '',
  default_rate_day: '',
  default_rate_night: '',
  default_rate_currency_code: 'UZS',
  sort_order: '',
  is_active: '1',
})

const togglingId = ref<number | null>(null)

const canUpdate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const canDelete = computed(() => auth.user?.permissions.includes('products.delete') ?? false)
const permissionHint = computed(() => t('engineering.operations.permissionHint'))
const canReorder = computed(() => canUpdate.value)

async function persistOperationOrder(
  _moved: OperationReference,
  _fromIndex: number,
  _toIndex: number,
): Promise<void> {
  if (!canUpdate.value) return
  const changed: OperationReference[] = []
  operations.value.forEach((item, index) => {
    const nextOrder = index + 1
    if ((item.sort_order ?? 0) !== nextOrder) {
      item.sort_order = nextOrder
      changed.push(item)
    }
  })
  if (!changed.length) return
  try {
    await Promise.all(
      changed.map((item) =>
        updateReferenceItem(client, 'operation', item.id, {
          code: item.code,
          name: item.name,
          default_rate_day: item.default_rate_day ?? null,
          default_rate_night: item.default_rate_night ?? null,
          default_rate_currency_code: item.default_rate_currency_code || 'UZS',
          sort_order: item.sort_order ?? 0,
          is_active: Number(item.is_active),
        }),
      ),
    )
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
    await loadOperations()
  }
}

const {
  draggingId: draggingOperationId,
  overId: dragOverOperationId,
  onDragStart: onOperationDragStart,
  onDragOver: onOperationDragOver,
  onDrop: onOperationDrop,
  onDragEnd: onOperationDragEnd,
} = useDragSort<OperationReference>(operations, {
  enabled: canReorder,
  onReorder: persistOperationOrder,
})
const filteredOperations = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return operations.value
  return operations.value.filter((item) =>
    [item.code, item.name].some((value) =>
      String(value ?? '').toLocaleLowerCase().includes(query),
    ),
  )
})
const visibleOperations = computed(() => filteredOperations.value)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(visibleOperations.value.length / pageSize.value)),
)
const paginatedOperations = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return visibleOperations.value.slice(start, start + pageSize.value)
})
const isEditing = computed(() => editingId.value != null)
const selectedCount = computed(() => selectedOperationIds.value.length)
const allVisibleSelected = computed(() =>
  Boolean(paginatedOperations.value.length)
  && paginatedOperations.value.every((item) => selectedOperationIds.value.includes(item.id)),
)

function resetForm(): void {
  editingId.value = null
  form.code = ''
  form.name = ''
  form.default_rate_day = ''
  form.default_rate_night = ''
  form.default_rate_currency_code = 'UZS'
  form.sort_order = ''
  form.is_active = '1'
  formError.value = ''
}

const GENERATED_STOPWORDS = new Set([
  'and', 'or', 'the', 'of', 'to', 'for', 'in', 'on', 'with', 'by', 'at', 'from',
  'va', 'ham', 'yoki', 'bilan', 'uchun', 'ichida',
  'и', 'или', 'для', 'с', 'на', 'в', 'при', 'от',
])

function generateCodeFromName(name: string): string {
  const words = name
    .trim()
    .toLowerCase()
    .split(/[\s\-_/]+/)
    .filter(Boolean)
  const significant = words.filter((word) => !GENERATED_STOPWORDS.has(word))
  const source = significant.length ? significant : words
  // First letters of up to 4 significant words
  let code = source
    .slice(0, 4)
    .map((word) => word.charAt(0))
    .join('')
  // Pad with more letters from the words until the code is 8 characters
  const remainder = source.join('').replace(/[^a-z0-9]/g, '')
  for (let i = 0; i < remainder.length && code.length < 8; i += 1) {
    code += remainder[i]
  }
  if (code.length < 2 && words.length) {
    code = words[0].slice(0, 8)
  }
  if (!code) {
    code = name.replace(/[^a-z0-9]/gi, '').slice(0, 8)
  }
  return code.slice(0, 8).toUpperCase() || 'OP'
}

function ensureUniqueOperationCode(code: string): string {
  const existing = new Set(
    operations.value
      .map((item) => item.code.trim().toUpperCase())
      .filter(Boolean),
  )
  if (!existing.has(code)) return code
  let suffix = 2
  let candidate = ''
  do {
    const suffixText = String(suffix)
    candidate = `${code.slice(0, 8 - suffixText.length)}${suffixText}`
    suffix += 1
  } while (existing.has(candidate))
  return candidate
}

watch(
  () => form.name,
  () => {
    if (editingId.value != null) return
    const name = form.name.trim()
    if (name && !form.code.trim()) {
      form.code = ensureUniqueOperationCode(generateCodeFromName(name))
    }
  },
)

function formatRate(value: number | null | undefined, currency?: string | null): string {
  if (value == null) return t('engineering.operations.notSet')
  const formatted = new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(value)
  return currency ? `${formatted} ${currency}` : formatted
}

async function toggleOperationStatus(item: OperationReference): Promise<void> {
  if (!canUpdate.value || togglingId.value != null) return
  togglingId.value = item.id
  errorMessage.value = ''
  try {
    const nextActive = !item.is_active
    await updateReferenceItem(client, 'operation', item.id, {
      code: item.code,
      name: item.name,
      default_rate_day: item.default_rate_day ?? null,
      default_rate_night: item.default_rate_night ?? null,
      default_rate_currency_code: item.default_rate_currency_code || 'UZS',
      sort_order: item.sort_order ?? 0,
      is_active: Number(nextActive),
    })
    item.is_active = nextActive ? 1 : 0
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    togglingId.value = null
  }
}

function startEdit(item: OperationReference): void {
  editingId.value = item.id
  form.code = item.code
  form.name = item.name
  form.default_rate_day = item.default_rate_day == null ? '' : String(item.default_rate_day)
  form.default_rate_night = item.default_rate_night == null ? '' : String(item.default_rate_night)
  form.default_rate_currency_code = item.default_rate_currency_code || 'UZS'
  form.sort_order = item.sort_order ? String(item.sort_order) : ''
  form.is_active = item.is_active ? '1' : '0'
  formError.value = ''
}

async function loadOperations(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  selectionMessage.value = ''
  try {
    operations.value = await listOperationReferences(client)
  } catch (error: unknown) {
    operations.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

function syncSelectedOperations(): void {
  const visibleIds = new Set(visibleOperations.value.map((item) => item.id))
  selectedOperationIds.value = selectedOperationIds.value.filter((id) => visibleIds.has(id))
}

async function submit(): Promise<void> {
  if (!canUpdate.value) return
  saving.value = true
  formError.value = ''
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      default_rate_day: form.default_rate_day === '' ? null : Number(form.default_rate_day),
      default_rate_night: form.default_rate_night === '' ? null : Number(form.default_rate_night),
      default_rate_currency_code: form.default_rate_currency_code.trim().toUpperCase() || 'UZS',
      sort_order: form.sort_order.trim() ? Number(form.sort_order) : 0,
      is_active: Number(form.is_active),
    }
    if (editingId.value == null) {
      await createReferenceItem(client, 'operation', payload)
    } else {
      await updateReferenceItem(client, 'operation', editingId.value, payload)
    }
    await loadOperations()
    resetForm()
  } catch (error: unknown) {
    formError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function removeOperation(item: OperationReference): Promise<void> {
  if (!canDelete.value) return
  if (!window.confirm(t('engineering.operations.confirmDelete', { name: item.name }))) return
  deletingId.value = item.id
  errorMessage.value = ''
  try {
    await deleteReferenceItem(client, 'operation', item.id)
    if (editingId.value === item.id) {
      resetForm()
    }
    await loadOperations()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deletingId.value = null
  }
}

function toggleOperationSelection(operationId: number, checked: boolean): void {
  if (checked) {
    if (!selectedOperationIds.value.includes(operationId)) {
      selectedOperationIds.value = [...selectedOperationIds.value, operationId]
    }
    return
  }
  selectedOperationIds.value = selectedOperationIds.value.filter((id) => id !== operationId)
}

function togglePageSelection(checked: boolean): void {
  const pageIds = paginatedOperations.value.map((item) => item.id)
  if (checked) {
    selectedOperationIds.value = Array.from(new Set([...selectedOperationIds.value, ...pageIds]))
    return
  }
  selectedOperationIds.value = selectedOperationIds.value.filter((id) => !pageIds.includes(id))
}

async function deleteSelectedOperations(): Promise<void> {
  if (!selectedCount.value || deleting.value || !canDelete.value) return
  if (!window.confirm(t('engineering.operations.confirmDeleteSelected', { count: selectedCount.value }))) return
  deleting.value = true
  errorMessage.value = ''
  selectionMessage.value = ''
  try {
    const deletedCount = selectedCount.value
    await Promise.all(selectedOperationIds.value.map((id) => deleteReferenceItem(client, 'operation', id)))
    if (editingId.value != null && selectedOperationIds.value.includes(editingId.value)) {
      resetForm()
    }
    selectedOperationIds.value = []
    selectionMessage.value = t('engineering.operations.deletedSelected', { count: deletedCount })
    await loadOperations()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deleting.value = false
  }
}

onMounted(loadOperations)
watch(search, () => {
  currentPage.value = 1
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    currentPage.value = Math.min(currentPage.value, totalPages.value)
  }, 300)
})
watch([visibleOperations, pageSize], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value)
  syncSelectedOperations()
})
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('engineering.operations.eyebrow') }}</p>
      <h2>{{ t('engineering.operations.title') }}</h2>
      <p>{{ t('engineering.operations.description') }}</p>
    </div>
    <div class="summary-tile">
      <ClipboardList :size="18" aria-hidden="true" />
      <div>
        <strong>{{ operations.length }}</strong>
        <span>{{ t('engineering.operations.count') }}</span>
      </div>
    </div>
  </div>

  <section class="workspace">
    <form class="editor-card" @submit.prevent="submit">
      <div class="section-heading">
        <div>
          <p class="eyebrow">{{ isEditing ? t('engineering.operations.editEyebrow') : t('engineering.operations.createEyebrow') }}</p>
          <h3>{{ isEditing ? t('engineering.operations.editTitle') : t('engineering.operations.createTitle') }}</h3>
        </div>
        <button v-if="isEditing" class="ghost-button" type="button" @click="resetForm">
          {{ t('engineering.action.cancel') }}
        </button>
      </div>

      <p v-if="formError" class="message message--error" role="alert">{{ formError }}</p>
      <p v-if="!canUpdate" class="message message--hint">{{ permissionHint }}</p>

      <div class="form-grid">
        <label>
          <span>{{ t('engineering.operations.name') }}</span>
          <input v-model="form.name" :disabled="saving || !canUpdate" type="text">
        </label>
        <label>
          <span>{{ t('engineering.operations.code') }}</span>
          <input v-model="form.code" :disabled="saving || !canUpdate" type="text" :placeholder="!isEditing ? t('engineering.operations.codePlaceholder') : undefined">
          <small v-if="!isEditing" class="field-hint">{{ t('engineering.operations.codeHint') }}</small>
        </label>
        <label>
          <span>{{ t('engineering.operations.sortOrder') }}</span>
          <input v-model="form.sort_order" :disabled="saving || !canUpdate" min="0" step="1" type="number">
        </label>
        <label>
          <span>{{ t('engineering.operations.dayRate') }}</span>
          <input v-model="form.default_rate_day" :disabled="saving || !canUpdate" min="0" step="0.01" type="number" inputmode="decimal" placeholder="0.00">
        </label>
        <label>
          <span>{{ t('engineering.operations.nightRate') }}</span>
          <input v-model="form.default_rate_night" :disabled="saving || !canUpdate" min="0" step="0.01" type="number" inputmode="decimal" placeholder="0.00">
        </label>
        <label>
          <span>{{ t('engineering.operations.rateCurrency') }}</span>
          <input v-model="form.default_rate_currency_code" :disabled="saving || !canUpdate" maxlength="3" type="text" placeholder="UZS">
          <small class="field-hint">{{ t('engineering.operations.rateHelp') }}</small>
        </label>
        <label>
          <span>{{ t('engineering.operations.status') }}</span>
          <button
            type="button"
            class="switch-button"
            :class="{ 'switch-button--on': form.is_active === '1' }"
            role="switch"
            :aria-checked="form.is_active === '1'"
            :aria-label="t('engineering.operations.toggleStatus')"
            :disabled="saving || !canUpdate"
            @click="form.is_active = form.is_active === '1' ? '0' : '1'"
          >
            <span class="switch-button__track" aria-hidden="true"><span class="switch-button__thumb" /></span>
            <span class="switch-button__label">{{ form.is_active === '1' ? t('engineering.operations.active') : t('engineering.operations.inactive') }}</span>
          </button>
        </label>
      </div>

      <div class="form-actions">
        <button
          class="primary-button"
          type="submit"
          :disabled="saving || !canUpdate || !form.code.trim() || !form.name.trim()"
          :title="!canUpdate ? permissionHint : undefined"
        >
          <Plus v-if="!isEditing" :size="16" aria-hidden="true" />
          <Pencil v-else :size="16" aria-hidden="true" />
          {{ saving ? t('engineering.operations.saving') : (isEditing ? t('engineering.operations.saveChanges') : t('engineering.operations.create')) }}
        </button>
      </div>
    </form>

    <section class="list-card">
      <div class="list-toolbar">
        <label class="search-field">
          <span>{{ t('engineering.operations.search') }}</span>
          <input v-model="search" type="search" :placeholder="t('engineering.operations.searchPlaceholder')">
        </label>
        <button class="ghost-button" type="button" :disabled="loading" @click="loadOperations">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ t('engineering.action.retry') }}
        </button>
      </div>

      <div v-if="errorMessage" class="message message--error" role="alert">{{ errorMessage }}</div>
      <div v-if="selectionMessage" class="message message--success" role="status">{{ selectionMessage }}</div>
      <div v-if="loading" class="table-state">{{ t('engineering.operations.loading') }}</div>
      <div v-else-if="!visibleOperations.length" class="table-state">{{ t('engineering.operations.empty') }}</div>
      <table v-else class="reference-table">
        <thead>
          <tr>
            <th class="drag-column" :title="canReorder ? t('engineering.action.dragHint') : undefined">
              <span v-if="canReorder" class="drag-handle drag-handle--header" aria-hidden="true"><GripVertical :size="15" /></span>
            </th>
            <th v-if="canDelete" class="select-column">
              <input
                :checked="allVisibleSelected"
                type="checkbox"
                :aria-label="t('engineering.operations.selectPageOperations')"
                @change="togglePageSelection(($event.target as HTMLInputElement).checked)"
              >
            </th>
            <th>{{ t('engineering.operations.code') }}</th>
            <th>{{ t('engineering.operations.name') }}</th>
            <th>{{ t('engineering.operations.sortOrder') }}</th>
            <th>{{ t('engineering.operations.dayRate') }}</th>
            <th>{{ t('engineering.operations.nightRate') }}</th>
            <th>{{ t('engineering.operations.status') }}</th>
            <th>{{ t('engineering.catalog.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedOperations"
            :key="item.id"
            class="sortable-row"
            :class="{ 'sortable-row--over': canReorder && dragOverOperationId === item.id && draggingOperationId !== item.id }"
            @dragover="onOperationDragOver($event, item)"
            @drop="onOperationDrop($event, item)"
          >
            <td class="drag-column">
              <span
                v-if="canReorder"
                class="drag-handle"
                draggable="true"
                :title="t('engineering.action.dragHint')"
                @dragstart="onOperationDragStart($event, item)"
                @dragend="onOperationDragEnd"
              >
                <GripVertical :size="15" aria-hidden="true" />
              </span>
            </td>
            <td v-if="canDelete" class="select-column">
              <input
                :checked="selectedOperationIds.includes(item.id)"
                type="checkbox"
                :aria-label="t('engineering.operations.selectOperation', { name: item.name })"
                @change="toggleOperationSelection(item.id, ($event.target as HTMLInputElement).checked)"
              >
            </td>
            <td :data-label="t('engineering.operations.code')">
              <strong>{{ item.code }}</strong>
              <small>#{{ item.id }}</small>
            </td>
            <td :data-label="t('engineering.operations.name')">{{ item.name }}</td>
            <td :data-label="t('engineering.operations.sortOrder')">{{ item.sort_order }}</td>
            <td :data-label="t('engineering.operations.dayRate')">{{ formatRate(item.default_rate_day, item.default_rate_currency_code) }}</td>
            <td :data-label="t('engineering.operations.nightRate')">{{ formatRate(item.default_rate_night, item.default_rate_currency_code) }}</td>
            <td :data-label="t('engineering.operations.status')">
              <button
                type="button"
                class="switch-button switch-button--small"
                :class="{ 'switch-button--on': Boolean(item.is_active) }"
                role="switch"
                :aria-checked="Boolean(item.is_active)"
                :aria-label="t('engineering.operations.toggleStatus')"
                :disabled="!canUpdate || togglingId === item.id"
                :title="!canUpdate ? permissionHint : undefined"
                @click="toggleOperationStatus(item)"
              >
                <span class="switch-button__track" aria-hidden="true"><span class="switch-button__thumb" /></span>
                <span class="switch-button__label">{{ item.is_active ? t('engineering.operations.active') : t('engineering.operations.inactive') }}</span>
              </button>
            </td>
            <td class="row-actions" :data-label="t('engineering.catalog.actions')">
              <button class="icon-button" type="button" :disabled="!canUpdate" :title="!canUpdate ? permissionHint : undefined" @click="startEdit(item)">
                <Pencil :size="16" aria-hidden="true" />
                {{ t('engineering.operations.edit') }}
              </button>
              <button class="icon-button icon-button--danger" type="button" :disabled="!canDelete || deletingId === item.id" :title="!canDelete ? permissionHint : undefined" @click="removeOperation(item)">
                <Trash2 :size="16" aria-hidden="true" />
                {{ t('engineering.operations.remove') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <PaginationControls
        v-if="!loading && visibleOperations.length"
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="visibleOperations.length"
        :page-size-options="[10, 25, 50]"
      />
      <div
        v-if="canDelete"
        class="selection-bar"
        :class="{ 'selection-bar--active': selectedCount > 0 }"
      >
        <span>{{ t('engineering.operations.selectedOperations', { count: selectedCount }) }}</span>
        <button
          class="icon-button icon-button--danger"
          type="button"
          :disabled="!selectedCount || deleting"
          @click="deleteSelectedOperations"
        >
          <Trash2 :size="16" aria-hidden="true" />
          {{ deleting ? t('engineering.operations.deletingSelected') : t('engineering.operations.deleteSelected') }}
        </button>
      </div>
    </section>
  </section>
</template>

<style scoped>
.page-heading,.section-heading,.list-toolbar,.form-actions,.row-actions{display:flex;gap:12px;align-items:center;justify-content:space-between}
.page-heading{margin-bottom:24px}
.workspace{display:grid;grid-template-columns:minmax(320px,380px) minmax(0,1fr);gap:18px}
.editor-card,.list-card{padding:18px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.eyebrow{margin:0 0 4px;color:#66726e;font-size:12px;font-weight:700;text-transform:uppercase}
h2,h3,p{margin:0}
.page-heading>div:first-child p:last-child{margin-top:8px;color:#66726e}
.summary-tile{display:inline-flex;gap:12px;align-items:center;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.summary-tile strong,td strong{display:block}
.summary-tile span,td small{color:#66726e;font-size:12px}
.form-grid{display:grid;gap:12px;margin-top:16px}
label{display:grid;gap:6px}
label span,th{color:#586660;font-size:12px;font-weight:700;text-transform:uppercase}
input,select,button{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}
.primary-button,.ghost-button,.icon-button{display:inline-flex;gap:8px;align-items:center;justify-content:center;width:auto;font-weight:700;cursor:pointer}
.primary-button{border:0;color:#fff;background:#3568a8}
.ghost-button,.icon-button{color:#285b9a}
.icon-button--danger{color:#8b3025}
.form-actions{justify-content:flex-start;margin-top:16px}
.search-field{flex:1}
.search-field input{width:100%}
.message{margin-top:16px;padding:12px 14px;border-radius:8px}
.message--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}
.message--success{border:1px solid #9ac7b5;color:#236451;background:#eef8f3}
.message--hint{border:1px solid #d7dfdc;color:#66726e;background:#f7faf9}
.list-card table{width:100%;margin-top:16px;border-collapse:collapse}
th,td{padding:12px;border-bottom:1px solid #e1e6e4;text-align:left;vertical-align:middle}
th{background:#f3f6f5}
.select-column{width:1%;white-space:nowrap}
.select-column input{width:16px;min-height:16px;padding:0}
.drag-column{width:36px;white-space:nowrap}
.drag-handle{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;color:#8a9691;cursor:grab;touch-action:none}
.drag-handle:hover{color:#285b9a;background:#eef4fb}
.drag-handle--header{cursor:default;color:#b6c0bc}
.drag-handle:active{cursor:grabbing}
.sortable-row--over{box-shadow:inset 3px 0 0 #3568a8;background:#f4f8fd}
.sortable-row--over td{background:transparent}
.switch-button{display:inline-flex;gap:8px;align-items:center;width:auto;min-height:32px;padding:4px 6px;border:0;background:transparent;cursor:pointer;font:inherit;font-weight:700}
.switch-button__track{position:relative;flex-shrink:0;width:36px;height:20px;border-radius:999px;background:#c2c9c6;transition:background .15s ease}
.switch-button__thumb{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgb(0 0 0 / .25);transition:transform .15s ease}
.switch-button--on .switch-button__track{background:#3568a8}
.switch-button--on .switch-button__thumb{transform:translateX(16px)}
.switch-button__label{color:#5f6b66;font-size:13px}
.switch-button--on .switch-button__label{color:#285b9a}
.switch-button--small{min-height:28px;padding:2px 4px}
.switch-button--small .switch-button__label{font-size:12px}
.field-hint{margin-top:2px;color:#8a9691;font-size:12px;font-weight:400;text-transform:none}
.table-state{display:grid;min-height:220px;color:#697671;place-items:center}
.selection-bar{display:flex;gap:12px;align-items:center;justify-content:space-between;margin-top:16px;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#f8faf9}
.selection-bar--active{border-color:#d7a8a1;background:#fff7f5}
button:disabled{cursor:not-allowed;opacity:.55}
@media (max-width:900px){.workspace{grid-template-columns:1fr}}
@media (max-width:640px){
  .page-heading,.section-heading,.list-toolbar,.selection-bar{align-items:stretch;flex-direction:column}
  .summary-tile{align-self:flex-start}
  .editor-card,.list-card{padding:14px}
  .reference-table{display:block;min-width:0;border:0;background:transparent}
  .reference-table thead{display:none}
  .reference-table tbody{display:grid;gap:10px}
  .reference-table tr{position:relative;display:grid;padding:12px;border:1px solid #d7dfdc;border-radius:10px;background:#fff}
  .reference-table td{display:grid;grid-template-columns:minmax(105px,.75fr) minmax(0,1.25fr);gap:10px;align-items:center;width:auto;height:auto;padding:7px 0!important;border-bottom:1px solid #edf0ef!important;white-space:normal;overflow-wrap:normal;word-break:normal}
  .reference-table td::before{content:attr(data-label);color:#66726e;font-size:10px;font-weight:700;letter-spacing:.035em;text-transform:uppercase}
  .reference-table td.drag-column{display:none}
  .reference-table td.select-column{display:block;position:absolute;top:10px;right:10px;padding:0!important;border:0!important}
  .reference-table td.select-column::before{display:none}
  .reference-table .row-actions{display:flex;justify-content:flex-end!important;padding-top:10px!important;border-bottom:0!important;white-space:normal}
  .reference-table .row-actions::before{margin-right:auto}
  .reference-table .switch-button{justify-content:flex-start;padding:0}
}
</style>
